import { z } from 'zod';
import { createOfferInputSchema } from './offer.js';

/**
 * SIL-2222: broker referral links and draft offers.
 *
 * A broker drafts an offer on behalf of a client (listing, amount, client
 * email). The client registers, then confirms in their own org: the confirm
 * creates the real offer. The broker never buys, pays or holds title.
 */

/** Org types a broker can refer a client into. 'broker' is deliberately absent. */
export const brokerClientOrgTypeSchema = z.enum(['business', 'individual']);
export type BrokerClientOrgType = z.infer<typeof brokerClientOrgTypeSchema>;

export const BROKER_NOTE_MAX_LENGTH = 500;

/**
 * Body for POST /api/client/brokerage/drafts. The amount reuses
 * createOfferInputSchema's amount schema so the two can never drift apart.
 */
export const createOfferDraftInputSchema = z.object({
  listingId: z.string().uuid(),
  amountUsd: createOfferInputSchema.shape.amountUsd,
  clientEmail: z.string().email(),
  clientOrgType: brokerClientOrgTypeSchema,
  note: z.string().max(BROKER_NOTE_MAX_LENGTH).optional(),
});
export type CreateOfferDraftInput = z.infer<typeof createOfferDraftInputSchema>;

export const offerDraftStatusSchema = z.enum([
  'sent',
  'registered',
  'confirmed',
  'declined',
  'expired',
  'cancelled',
  'listing_unavailable',
]);
export type OfferDraftStatus = z.infer<typeof offerDraftStatusSchema>;

/**
 * Broker-side draft row (broker_my_drafts). Name, price and offer status are
 * null outside their visibility windows, and `clientEmail` is null after the
 * purge; the broker never sees seller data.
 */
export const offerDraftSchema = z.object({
  draftId: z.string().uuid(),
  status: offerDraftStatusSchema,
  listingId: z.string().uuid(),
  amountUsd: z.number().nullable(),
  clientEmail: z.string().nullable(),
  clientName: z.string().nullable(),
  offerStatus: z.string().nullable(),
  createdAt: z.string(),
  expiresAt: z.string(),
});
export type OfferDraft = z.infer<typeof offerDraftSchema>;

/** Body for POST /api/client/brokerage/referral-links (plain, no draft). */
export const createReferralLinkInputSchema = z.object({
  organizationType: brokerClientOrgTypeSchema,
  note: z.string().max(BROKER_NOTE_MAX_LENGTH).optional(),
});
export type CreateReferralLinkInput = z.infer<typeof createReferralLinkInputSchema>;

/** Client-side view of a broker draft awaiting the client's confirm or decline. */
export const pendingBrokerDraftSchema = z.object({
  draftId: z.string().uuid(),
  listingId: z.string().uuid(),
  amountUsd: z.number(),
  brokerName: z.string(),
  note: z.string().nullable(),
  expiresAt: z.string(),
});
export type PendingBrokerDraft = z.infer<typeof pendingBrokerDraftSchema>;

/** Result of POST /api/client/broker-drafts/:id/confirm. */
export const confirmDraftResultSchema = z.object({
  offerId: z.string().uuid(),
});
export type ConfirmDraftResult = z.infer<typeof confirmDraftResultSchema>;

/**
 * Confirm and decline take NO body: terms come only from the locked draft row,
 * so a client can never alter the broker's listing or amount. Strict, so a body
 * carrying `amountUsd` or `listingId` is rejected instead of silently ignored.
 */
export const confirmDraftBodySchema = z.object({}).strict();
export const declineDraftBodySchema = confirmDraftBodySchema;
